import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Trash2 } from "lucide-react";
import { useOrganization } from "@/hooks/useOrganization";
import { useAuth } from "@/hooks/useAuthApi";
import { getUserOrganizationCount, deleteOrganization } from "@/services/organizationService";
import { toast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const DangerZone = () => {
  const { currentOrganization, isAdmin, refreshOrganizations } = useOrganization();
  const { user, deleteUserAccount } = useAuth();
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLastOrganization, setIsLastOrganization] = useState(false);
  const [organizationCount, setOrganizationCount] = useState(0);

  // Check if this is the user's last organization when dialog opens
  const handleDialogOpenChange = async (open: boolean) => {
    if (open && user && currentOrganization) {
      try {
        const count = await getUserOrganizationCount(user.uid);
        setOrganizationCount(count);
        setIsLastOrganization(count === 1);
      } catch (error) {
        console.error('Error checking organization count:', error);
        setIsLastOrganization(false);
        setOrganizationCount(0);
      }
    }
  };

  const handleDeleteOrganization = async () => {
    if (!currentOrganization || !user) return;

    // Validate confirmation text
    if (confirmationText !== currentOrganization.name) {
      toast({
        title: "Error",
        description: "Organization name doesn't match. Please type the exact organization name to confirm deletion.",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsDeleting(true);

      // Delete the organization with cascade
      await deleteOrganization(currentOrganization.id);

      toast({
        title: "Organization deleted",
        description: `${currentOrganization.name} has been permanently deleted.`,
      });

      if (isLastOrganization) {
        // Delete user account if this was their last organization
        toast({
          title: "Deleting your account",
          description: "This was your last organization. Your account will be deleted automatically.",
        });

        await deleteUserAccount();
        
        // User will be automatically logged out by the auth system
      } else {
        // Refresh organizations to remove the deleted one
        await refreshOrganizations();
      }

    } catch (error) {
      console.error('Error deleting organization:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete organization.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
      setConfirmationText('');
    }
  };

  // Only show to admin users
  if (!isAdmin || !currentOrganization) {
    return null;
  }

  return (
    <Card className="border-destructive/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-5 w-5" />
          Danger Zone
        </CardTitle>
        <CardDescription>
          Permanently delete this organization and all associated data. This action cannot be undone.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <AlertDialog onOpenChange={handleDialogOpenChange}>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" className="gap-2">
              <Trash2 className="h-4 w-4" />
              Delete Organization
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-destructive">
                Delete Organization: {currentOrganization.name}
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <p>
                  This will permanently delete the organization and all associated data including:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>All session recordings and analytics</li>
                  <li>All organization members and invitations</li>
                  <li>Organization logo and settings</li>
                </ul>
                {isLastOrganization && (
                  <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                    <p className="font-semibold text-destructive">
                      ⚠️ Account Deletion Warning
                    </p>
                    <p className="text-sm text-destructive/80">
                      This is your only organization ({organizationCount} total). Deleting it will also permanently delete your entire account and you will be logged out immediately.
                    </p>
                  </div>
                )}
                <p className="font-semibold mt-4">
                  This action cannot be undone.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            
            <div className="my-4">
              <Label htmlFor="confirmation" className="text-sm font-medium">
                Type <span className="font-mono font-bold">{currentOrganization.name}</span> to confirm:
              </Label>
              <Input
                id="confirmation"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder={currentOrganization.name}
                className="mt-2"
                disabled={isDeleting}
              />
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteOrganization}
                disabled={confirmationText !== currentOrganization.name || isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? "Deleting..." : "Delete Organization"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};
